import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, shareReplay, tap, throwError } from 'rxjs';

import { environment } from '../../../config/environment';
import { BackendResponse } from '../models/api-response.model';
import {
  AuthProvidersInfo,
  AuthResponse,
  AuthToken,
  ChangePasswordRequest,
  LoginCredentials,
  RegisterData,
  UpdateProfileRequest,
  User,
} from '../models/user.model';

const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authBase = `${environment.apiUrl}/auth`;
  private readonly usersBase = `${environment.apiUrl}/users`;

  private readonly accessTokenSignal = signal<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_KEY) : null
  );
  private readonly currentUserSignal = signal<User | null>(null);
  private providers$: Observable<AuthProvidersInfo> | null = null;

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.accessTokenSignal() !== null);
  readonly isAdmin = computed(() => this.currentUserSignal()?.roles.includes('ROLE_ADMIN') ?? false);

  /**
   * Hydrates the current user from `/api/auth/me` if a token is present.
   * Intended to be called from APP_INITIALIZER, not from the constructor —
   * keeps service construction free of HTTP side-effects and avoids logging
   * the user out on transient network errors.
   */
  initializeFromStorage(): Observable<User | null> {
    if (!this.accessTokenSignal()) {
      return of(null);
    }
    return this.fetchCurrentUser().pipe(
      catchError((err: HttpErrorResponse) => {
        // Only wipe tokens when the backend actually rejects them.
        if (err.status === 401) {
          this.clearSession();
        }
        return of(null);
      })
    );
  }

  getAccessToken(): string | null {
    return this.accessTokenSignal();
  }

  getRefreshToken(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(REFRESH_TOKEN_KEY) : null;
  }

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http
      .post<BackendResponse<AuthResponse>>(`${this.authBase}/login`, credentials)
      .pipe(
        map(response => this.unwrap(response)),
        tap(auth => this.applyAuthResponse(auth))
      );
  }

  googleLogin(idToken: string): Observable<AuthResponse> {
    return this.http
      .post<BackendResponse<AuthResponse>>(`${this.authBase}/google`, { idToken })
      .pipe(
        map(response => this.unwrap(response)),
        tap(auth => this.applyAuthResponse(auth))
      );
  }

  register(data: RegisterData): Observable<User> {
    return this.http
      .post<BackendResponse<User>>(`${this.authBase}/register`, data)
      .pipe(map(response => this.unwrap(response)));
  }

  forgotPassword(email: string): Observable<void> {
    return this.http
      .post<BackendResponse<void>>(`${this.authBase}/forgot-password`, { email })
      .pipe(map(response => this.unwrap(response)));
  }

  resetPassword(token: string, password: string): Observable<void> {
    return this.http
      .post<BackendResponse<void>>(`${this.authBase}/reset-password`, { token, password })
      .pipe(map(response => this.unwrap(response)));
  }

  resendVerification(email: string): Observable<void> {
    return this.http
      .post<BackendResponse<void>>(`${this.authBase}/resend-verification`, { email })
      .pipe(map(response => this.unwrap(response)));
  }

  verifyEmail(token: string): Observable<void> {
    const params = new URLSearchParams({ token });
    return this.http
      .get<BackendResponse<void>>(`${this.authBase}/verify-email?${params.toString()}`)
      .pipe(map(response => this.unwrap(response)));
  }

  refreshTokens(): Observable<AuthToken> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'No refresh token' }));
    }
    return this.http
      .post<BackendResponse<AuthResponse>>(`${this.authBase}/refresh`, { refreshToken })
      .pipe(
        map(response => this.unwrap(response)),
        tap(auth => this.applyAuthResponse(auth))
      );
  }

  fetchCurrentUser(): Observable<User> {
    return this.http.get<BackendResponse<User>>(`${this.authBase}/me`).pipe(
      map(response => this.unwrap(response)),
      tap(user => this.currentUserSignal.set(user))
    );
  }

  updateProfile(data: UpdateProfileRequest): Observable<User> {
    return this.http
      .patch<BackendResponse<User>>(`${this.usersBase}/me`, data)
      .pipe(
        map(response => this.unwrap(response)),
        tap(user => this.currentUserSignal.set(user))
      );
  }

  changePassword(data: ChangePasswordRequest): Observable<void> {
    return this.http
      .patch<BackendResponse<void>>(`${this.usersBase}/me/password`, data)
      .pipe(map(response => this.unwrap(response)));
  }

  /** Cached: backend providers config rarely changes within a session. */
  fetchProviders(): Observable<AuthProvidersInfo> {
    if (!this.providers$) {
      this.providers$ = this.http
        .get<BackendResponse<AuthProvidersInfo>>(`${this.authBase}/providers`)
        .pipe(map(response => this.unwrap(response)), shareReplay(1));
    }
    return this.providers$;
  }

  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    const cleanup = () => {
      this.clearSession();
      this.router.navigate(['/auth/login']);
    };

    if (!refreshToken) {
      cleanup();
      return of(void 0);
    }

    return this.http
      .post<BackendResponse<void>>(`${this.authBase}/logout`, { refreshToken })
      .pipe(
        map(response => this.unwrap(response)),
        catchError(() => of(void 0)),
        tap(() => cleanup())
      );
  }

  setTokens(token: AuthToken): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(ACCESS_TOKEN_KEY, token.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, token.refreshToken);
    this.accessTokenSignal.set(token.accessToken);
  }

  clearSession(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    this.accessTokenSignal.set(null);
    this.currentUserSignal.set(null);
  }

  private applyAuthResponse(auth: AuthResponse): void {
    this.setTokens(auth);
    this.currentUserSignal.set(auth.user);
  }

  private unwrap<T>(response: BackendResponse<T>): T {
    if (response.isError) {
      throw new HttpErrorResponse({
        status: 0,
        statusText: response.errorCode ?? 'Backend error',
        error: response,
      });
    }
    return response.result as T;
  }
}
